import ENVIRONMENT from '../../environment/environment';

const AIHUB_ENV_KEYS = [
  'AIHUB_BASE_URL',
  'NEXT_PUBLIC_AIHUB_API_ORIGIN',
  'CONFIG_NEXT_PUBLIC_AIHUB_API_BASE_URL',
] as const;

/** Linux dev (API = internal.toppion) — engine MS.AICoaching trên VPS, không qua FW gateway. */
export const DEFAULT_AIHUB_ORIGIN_INTERNAL_DEV = 'http://localhost:8001/';

const cleanAiHubOrigin = (raw: string): string =>
  raw.trim().replace(/\/api$/i, '').replace(/\/$/, '');

const readWindowEnv = (): Record<string, string | undefined> | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { __ENV__?: Record<string, string | undefined> }).__ENV__;
};

const isInternalToppionApiBase = (base: string): boolean =>
  /internal\.toppion\.com\.vn/i.test(base);

const resolveApiBaseHint = (): string => {
  const env = readWindowEnv();
  const fromWindow =
    env?.CONFIG_NEXT_PUBLIC_API_BASE_URL?.trim() ||
    env?.API_BASE_URL?.trim() ||
    env?.BASE_URL?.trim() ||
    '';
  if (fromWindow) return fromWindow.replace(/\/api\/?$/i, '');
  return (ENVIRONMENT.BASE_URL || ENVIRONMENT.API_HOST.replace(/\/api\/?$/i, '')).trim();
};

const defaultAiHubWhenInternalApi = (): string => {
  if (!isInternalToppionApiBase(resolveApiBaseHint())) return '';
  return DEFAULT_AIHUB_ORIGIN_INTERNAL_DEV;
};

/** Đọc origin AI Hub lúc gọi API (env.js / build env / container inject). */
const readAiHubOriginRaw = (): string => {
  const env = readWindowEnv();
  if (env) {
    for (const key of AIHUB_ENV_KEYS) {
      const value = env[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }

  for (const key of AIHUB_ENV_KEYS) {
    const value = process.env[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  if (ENVIRONMENT.AIHUB_BASE_URL?.trim()) {
    return ENVIRONMENT.AIHUB_BASE_URL;
  }

  return defaultAiHubWhenInternalApi();
};

/** Origin engine AI Hub (không có `/api`). Rỗng → FE gọi `/api/v1/aihub` qua API gateway chung. */
export const resolveAiHubApiOrigin = (): string => {
  const raw = readAiHubOriginRaw();
  return raw ? cleanAiHubOrigin(raw) : '';
};

export const resolveAiHubApiHost = (): string => {
  const origin = resolveAiHubApiOrigin();
  return origin ? `${origin}/api` : '';
};

/** Header gửi kèm mọi request AI Hub: gateway API của môi trường đang chạy (prod / dev linux / staging). Một engine
 *  phục vụ nhiều môi trường nên engine dùng giá trị này để gọi MS.Organization (kiểm quyền) đúng môi trường. */
export const AIHUB_GATEWAY_HEADER = 'X-Toppion-Api-Base';

/** Gateway `…/api` của môi trường hiện tại (ENVIRONMENT.API_HOST); rỗng khi không phải URL tuyệt đối. */
export const resolveToppionApiGateway = (): string => {
  const host = String(ENVIRONMENT.API_HOST || '').trim().replace(/\/+$/, '');
  return /^https?:\/\//i.test(host) ? host : '';
};

/** Header gateway cho request AI Hub (trống thì không gửi). */
export const aiHubGatewayHeaders = (): Record<string, string> => {
  const gateway = resolveToppionApiGateway();
  return gateway ? { [AIHUB_GATEWAY_HEADER]: gateway } : {};
};

export const isAiHubApiPath = (url: string | undefined): boolean => {
  if (!url) return false;
  const path = url.startsWith('http') ? new URL(url, 'http://local').pathname : url;
  return /^\/v1\/aihub\b/i.test(path);
};
